import {
  CollisionDetection,
  DndContext,
  closestCenter,
  pointerWithin,
} from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import AddIcon from "@mui/icons-material/Add";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import {
  useBatchFetchPageBlocks,
  useBatchFetchSubPages,
  useMoveNoteBlock,
  useNotebookPageAccess,
  usePageBlocksLoad,
  useSelectedNotebookPage,
} from "../../hook/page";
import { LoadBoundary } from "../brand/LoadBoundary";
import { isNotePageData } from "../../types/note";
import { isNotePageBlock } from "../../types/notebook/note/block/page";
import { NotePageContainer, NotePageHeader } from "../note";
import { BlockDragProvider, useBlockDrag } from "../../context/blockDrag";
import { EndDropZone, PageBlockComponent } from "./block";
import { AddBlockMenu } from "../menu/page/block";
import { PageComponentProperty } from "./component";
import { NoteProperties } from "./property";
import { PageProperties } from "./pageProp";
import { useMenu } from "../../hook/menu";

const END_ZONE_ID = "__drag-end__";

const collisionDetection: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) return pointer;
  return closestCenter(args);
};

const NotebookNotePageInner: React.FC = () => {
  const moveNoteBlock = useMoveNoteBlock();
  const page = useSelectedNotebookPage();
  const pageId = page?.["page-id"];
  const pageData = page?.["page-data"];
  const { writeAccess } = useNotebookPageAccess(pageId);
  const { pageBlocks, status: blocksStatus, fetchBlocks } = usePageBlocksLoad(pageId);

  // Collect all sub-page IDs from page-type blocks so we can batch-fetch them
  // with a single ADD_NOTEBOOK_PAGES dispatch instead of one per block.
  const subPageIds = useMemo(
    () =>
      (pageBlocks ?? [])
        .filter(isNotePageBlock)
        .map((b) => b["sub-page-id"])
        .filter(Boolean),
    [pageBlocks],
  );
  const subPageIdsKey = subPageIds.join(",");

  const batchFetchSubPages = useBatchFetchSubPages(subPageIds);
  useEffect(() => {
    batchFetchSubPages();
  }, [subPageIdsKey]); // intentional: fire on ID changes, not function ref changes

  // Pre-fetch the page-block content for all sub-pages in one batch dispatch
  // so child NotePageBlockComponents skip their individual fetch.
  const batchFetchPageBlocks = useBatchFetchPageBlocks(subPageIds);
  useEffect(() => {
    batchFetchPageBlocks();
  }, [subPageIdsKey]); // intentional: fire on ID changes, not function ref changes

  const { activeId, overId, onDragStart, onDragMove, onDragEnd, onDragCancel } = useBlockDrag();

  const {
    handleOpen: emptyAddHandleOpen,
    open: emptyAddOpen,
    onClose: emptyAddOnClose,
    anchorEl: emptyAddAnchorEl,
  } = useMenu();

  if (!pageData || !isNotePageData(pageData)) {
    return <></>;
  }

  if (blocksStatus !== "ready") {
    return (
      <LoadBoundary
        status={blocksStatus}
        onRetry={fetchBlocks}
        label="Loading page"
        errorMessage="Could not load this page."
        testId="page-blocks-loading"
        fill
      />
    );
  }

  const blocks = pageBlocks ?? [];
  const blockIds = blocks.map((b) => b["block-id"]);
  const sortableIds = [...blockIds, END_ZONE_ID];

  const blockComponents = blocks.map((block, i) => (
    <PageBlockComponent
      key={block["block-id"]}
      block={block}
      blockId={block["block-id"]}
      blockIndex={i}
      showDropMarkerAbove={overId === block["block-id"]}
      writeAccess={writeAccess}
    />
  ));

  const emptyState =
    blocks.length === 0 ? (
      <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "center", alignItems: "center", gap: 1 }}>
        <Typography color="text.disabled">Notebook is empty</Typography>
        {writeAccess && (
          <>
            <AddBlockMenu open={emptyAddOpen} anchorEl={emptyAddAnchorEl} onClose={emptyAddOnClose} blockIndex={0} />
            <IconButton
              data-testid="add-block-empty-page"
              aria-label="add-block-empty-page"
              size="small"
              onClick={(e) => emptyAddHandleOpen(e)}
              sx={{ opacity: 0.4, "&:hover": { opacity: 0.8 } }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
    ) : null;

  return (
    <NotePageContainer>
      <NotePageHeader pageId={pageId} />
      <PageComponentProperty />
      <NoteProperties />
      <PageProperties />
      <Divider />
      {emptyState}
      {writeAccess && blocks.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <DndContext
            collisionDetection={collisionDetection}
            onDragStart={onDragStart}
            onDragMove={(e) => onDragMove(e, blockIds, END_ZONE_ID)}
            onDragEnd={(e) => onDragEnd(e, blockIds, moveNoteBlock, END_ZONE_ID, blocks.length)}
            onDragCancel={onDragCancel}
          >
            <SortableContext items={sortableIds}>
              {blockComponents}
              <EndDropZone
                id={END_ZONE_ID}
                active={activeId !== null && overId === END_ZONE_ID}
              />
            </SortableContext>
          </DndContext>
        </Box>
      )}
      {!writeAccess && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {blockComponents}
        </Box>
      )}
    </NotePageContainer>
  );
};

export const NotebookNotePage: React.FC = () => {
  return (
    <BlockDragProvider>
      <NotebookNotePageInner />
    </BlockDragProvider>
  );
};
